import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import publicAxios from '../../utils/publicAxios';
import AuthContext from '../../context/AuthContext';
import LogoImage from '../common/LogoImage';
import MasterServicesAgreement from '../legal/MasterServicesAgreement';
import SubscriptionSelector from '../payment/SubscriptionSelector';
import './Auth.css';

// Stripe Payment Links
const PAYMENT_LINKS = {
  Monthly: 'https://buy.stripe.com/3cI8wO2OjchrcXmeHC2Ry06',
  SingleOrg: 'https://buy.stripe.com/28E6oGbkP0yJ7D21UQ2Ry04',
  MultiOrg: 'https://buy.stripe.com/00wbJ0ex1a9jaPedDy2Ry05'
};

const Register = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const invitationToken = queryParams.get('token');
  const [isInvitation, setIsInvitation] = useState(false);
  const [invitationData, setInvitationData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    industryType: '',
    organization: '',
    agreeToTerms: invitationToken ? true : false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [msaModalOpen, setMsaModalOpen] = useState(false);
  const [msaViewed, setMsaViewed] = useState(invitationToken ? true : false);
  
  // Registration step management
  const [currentStep, setCurrentStep] = useState(1); // 1: Info, 2: Subscription, 3: Payment
  const [selectedSubscription, setSelectedSubscription] = useState('SingleOrg');
  const [clientSecret, setClientSecret] = useState('');
  
  const { setIsAuthenticated, setUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const { name, email, industryType, organization, agreeToTerms } = formData;

  const onChange = e => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  // Fetch invitation data if token is present
  useEffect(() => {
    const fetchInvitationData = async () => {
      if (!invitationToken) return;
      
      try {
        setLoading(true);
        const res = await axios.get(`/api/invitations/token/${invitationToken}`);
        
        if (res.data && res.data.invitation) {
          setInvitationData(res.data.invitation);
          setIsInvitation(true);
          
          // Pre-fill the email field with the invited email
          setFormData(prev => ({
            ...prev,
            email: res.data.invitation.email,
            organization: res.data.invitation.organizationName,
            agreeToTerms: true
          }));
        }
      } catch (err) {
        setError(err.response?.data?.msg || 'Invalid or expired invitation link');
      } finally {
        setLoading(false);
      }
    };
    
    if (invitationToken) {
      fetchInvitationData();
    }
  }, [invitationToken]);

  // Handle form submission for each step
  const onSubmit = async e => {
    e.preventDefault();
    setError('');

    // For invitation flow, skip subscription and payment
    if (isInvitation && invitationToken) {
      handleInvitationAcceptance();
      return;
    }

    // Handle each step of the registration process
    switch (currentStep) {
      case 1: // User information step


        // Validate industry type is selected
        if (!industryType) {
          setError('Please select your industry type');
          return;
        }
        
        // Validate user has agreed to the terms
        if (!agreeToTerms) {
          setError('You must agree to the Master Services Agreement to continue');
          return;
        }

        // Move to subscription selection step
        setCurrentStep(2);
        break;

      case 2: // Subscription selection step
        // Move to payment step directly
        setCurrentStep(3);
        break;

      case 3: // Payment processing step is handled by the PaymentForm component
        // The actual registration happens in the completeRegistration function
        // which is called after successful payment
        break;

      default:
        break;
    }
  };

  // Handle invitation acceptance (no payment required)
  const handleInvitationAcceptance = async () => {
    setLoading(true);
    try {
      // Accept invitation
      const res = await axios.post(`/api/invitations/accept/${invitationToken}`, {
        name
      });
      
      // Save token to localStorage
      localStorage.setItem('token', res.data.token);
      
      // Set auth token header
      axios.defaults.headers.common['x-auth-token'] = res.data.token;
      
      // Update auth state
      setIsAuthenticated(true);
      setUser(res.data.user);
      
      // Redirect to dashboard
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to accept invitation. Please try again.');
      setLoading(false);
    }
  };

  // Handle continue to payment button click
  const handleContinueToPayment = async (subscriptionType) => {
    // Validate form data
    if (!name || !email || !industryType || !organization || !agreeToTerms) {
      setError('Please complete all required fields');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      console.log('Creating pending registration with subscription type:', subscriptionType);
      
      // Create a pending registration
      const registrationData = {
        name,
        email,
        industryType,
        organization,
        subscriptionTier: subscriptionType
      };
      
      console.log('Registration data:', registrationData);
      
      const response = await publicAxios.post('/api/pending-registrations', registrationData);
      
      // Get the registration token
      const { token } = response.data;
      
      // Store registration data in localStorage for retrieval after payment
      localStorage.setItem('pendingRegistrationToken', token);
      localStorage.setItem('pendingRegistrationEmail', email);
      
      console.log('Redirecting to Stripe payment page with token:', token);
      console.log('Payment URL:', PAYMENT_LINKS[subscriptionType]);
      
      // Redirect to the appropriate Stripe payment link
      if (PAYMENT_LINKS[subscriptionType]) {
        window.location.href = PAYMENT_LINKS[subscriptionType];
      } else {
        setError(`Payment link not found for subscription type: ${subscriptionType}`);
        setLoading(false);
      }
    } catch (err) {
      console.error('Registration error:', err);
      console.error('Error response:', err.response?.data);
      setLoading(false);
      setError(err.response?.data?.msg || 'Registration failed. Please try again.');
    }
  };

  // Complete registration after successful payment
  const completeRegistration = async (paymentMethodId, paymentDetails) => {
    try {
      setLoading(true);
      setError('');
      
      // Create the user account with subscription information
      const res = await axios.post('/api/users', {
        name,
        email,
        industryType,
        organization,
        subscriptionTier: selectedSubscription,
        paymentMethodId,
        discounted: paymentDetails?.discounted || false,
        discountAmount: paymentDetails?.discountAmount || 0,
        finalPrice: paymentDetails?.finalPrice || null,
        couponApplied: paymentDetails?.discounted ? 'PROMO500' : null
      });
      
      // Set authentication state
      setIsAuthenticated(true);
      setUser(res.data.user);
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.msg || 'Registration failed. Please try again.');
      setLoading(false);
    }
  };

  // Render different content based on the current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // User information step
        return (
          <form onSubmit={onSubmit} style={styles.form}>
            <div style={styles.formGroup}>
              <label htmlFor="name" style={styles.label}>Full Name</label>
              <input
                type="text"
                name="name"
                id="name"
                value={name}
                onChange={onChange}
                required
                style={styles.input}
              />
            </div>
            <div style={styles.formGroup}>
              <label htmlFor="email" style={styles.label}>Email Address</label>
              <input
                type="email"
                name="email"
                id="email"
                value={email}
                onChange={onChange}
                required
                disabled={isInvitation}
                style={{
                  ...styles.input,
                  backgroundColor: isInvitation ? '#f5f5f5' : 'white'
                }}
              />
              {isInvitation && (
                <small style={{ color: '#666', marginTop: '5px' }}>
                  Email address is pre-filled from your invitation.
                </small>
              )}
            </div>
            
            {!isInvitation && (
              <>
                <div style={styles.formGroup}>
                  <label htmlFor="organization" style={styles.label}>Organization Name</label>
                  <input
                    type="text"
                    name="organization"
                    id="organization"
                    value={organization}
                    onChange={onChange}
                    required
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label htmlFor="industryType" style={styles.label}>Industry Type</label>
                  <select
                    name="industryType"
                    id="industryType"
                    value={industryType}
                    onChange={onChange}
                    required
                    style={styles.select}
                  >
                    <option value="">Select Industry Type</option>
                    <option value="Aerospace & Defense">Aerospace & Defense</option>
                    <option value="Agriculture">Agriculture</option>
                    <option value="Automotive">Automotive</option>
                    <option value="Banking & Financial Services">Banking & Financial Services</option>
                    <option value="Chemicals">Chemicals</option>
                    <option value="Construction & Engineering">Construction & Engineering</option>
                    <option value="Consumer Goods">Consumer Goods</option>
                    <option value="Education">Education</option>
                    <option value="Energy & Utilities">Energy & Utilities</option>
                    <option value="Entertainment & Media">Entertainment & Media</option>
                    <option value="Food & Beverage">Food & Beverage</option>
                    <option value="Government">Government</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Hospitality & Tourism">Hospitality & Tourism</option>
                    <option value="Information Technology">Information Technology</option>
                    <option value="Insurance">Insurance</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="Mining & Metals">Mining & Metals</option>
                    <option value="Non-Profit">Non-Profit</option>
                    <option value="Pharmaceuticals">Pharmaceuticals</option>
                    <option value="Professional Services">Professional Services</option>
                    <option value="Real Estate">Real Estate</option>
                    <option value="Retail & E-Commerce">Retail & E-Commerce</option>
                    <option value="Telecommunications">Telecommunications</option>
                    <option value="Transportation & Logistics">Transportation & Logistics</option>
                    <option value="Utilities">Utilities</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </>
            )}
            
            {!isInvitation && (
              <div style={{ marginBottom: '20px' }}>
                <p style={{ fontSize: '0.95rem', marginBottom: '15px', color: '#4C5454', lineHeight: '1.4' }}>
                  By signing up, you agree to the 
                  <span 
                    onClick={() => setMsaModalOpen(true)} 
                    style={{ color: '#243444', fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    {' '}Master Services Agreement
                  </span> 
                  {' '}and acknowledge that your assessment data may be processed by OpenAI solely for report generation.
                </p>
                
                <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '10px' }}>
                  <input
                    type="checkbox"
                    name="agreeToTerms"
                    id="agreeToTerms"
                    checked={agreeToTerms}
                    onChange={onChange}
                    required
                    disabled={!msaViewed}
                    style={{ 
                      marginRight: '10px', 
                      marginTop: '3px',
                      cursor: msaViewed ? 'pointer' : 'not-allowed',
                      opacity: msaViewed ? 1 : 0.6
                    }}
                  />
                  <label 
                    htmlFor="agreeToTerms" 
                    style={{ 
                      fontSize: '0.95rem', 
                      color: '#243444',
                      cursor: msaViewed ? 'pointer' : 'not-allowed',
                      opacity: msaViewed ? 1 : 0.8
                    }}
                  >
                    I have read and agree to the Master Services Agreement
                    {!msaViewed && (
                      <span style={{ color: '#7C1227', marginLeft: '5px', fontSize: '0.85rem' }}>
                        (Please view the agreement first)
                      </span>
                    )}
                  </label>
                </div>
              </div>
            )}
            
            <button
              type="submit"
              style={{
                ...styles.button,
                opacity: agreeToTerms ? 1 : 0.7,
                cursor: agreeToTerms ? 'pointer' : 'not-allowed'
              }}
              disabled={loading || !agreeToTerms}
            >
              {loading ? 'Processing...' : isInvitation ? 'Accept Invitation' : 'Continue'}
            </button>
          </form>
        );
        
      case 2: // Subscription selection step
        return (
          <div style={styles.form}>
            <h3 style={styles.stepTitle}>Select Your Subscription</h3>
            <SubscriptionSelector 
              onSelectTier={tier => setSelectedSubscription(tier)} 
              selectedTier={selectedSubscription} 
            />
            
            <div style={styles.navigationButtons}>
              <button 
                onClick={() => setCurrentStep(1)} 
                style={styles.backButton}
                disabled={loading}
              >
                Back
              </button>
              <button 
                onClick={onSubmit} 
                style={styles.continueButton}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Continue to Payment'}
              </button>
            </div>
          </div>
        );
        
      case 3: // Payment step
        return (
          <div>
            <h3 style={styles.stepTitle}>Step 3: Payment Information</h3>
            
            <div style={styles.paymentContainer}>
              <p style={styles.paymentMessage}>
                You're almost done! Click the button below to proceed to our secure payment page.
              </p>
              
              <p style={styles.paymentDetails}>
                <strong>Selected Plan:</strong> {
                  selectedSubscription === 'Monthly' ? 'Monthly Subscription' :
                  selectedSubscription === 'SingleOrg' ? 'Single Organization' : 'Multiple Organizations'
                }<br />
                <strong>Price:</strong> {
                  selectedSubscription === 'Monthly' ? '$250/month' :
                  selectedSubscription === 'SingleOrg' ? '$2,500/year' : '$6,500/year'
                }<br />
                <strong>Users Included:</strong> {
                  selectedSubscription === 'Monthly' ? 'Single user' : '5 users per subscription'
                }
              </p>
              
              <button 
                type="button" 
                onClick={() => handleContinueToPayment(selectedSubscription)}
                style={styles.paymentButton}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Proceed to Payment'}
              </button>
              
              <p style={styles.secureNote}>
                🔒 Your payment will be securely processed by Stripe.
              </p>
            </div>
            
            <div style={styles.navigationButtons}>
              <button 
                type="button" 
                onClick={() => setCurrentStep(2)}
                style={styles.backButton}
                disabled={loading}
              >
                Back
              </button>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  // Render step indicators
  const renderStepIndicators = () => {
    const steps = [
      { number: 1, label: 'Account Details' },
      { number: 2, label: 'Select Plan' },
      { number: 3, label: 'Payment' }
    ];
    
    return (
      <div style={styles.stepIndicators}>
        {steps.map((step) => (
          <div key={step.number} style={styles.stepIndicatorContainer}>
            <div 
              style={{
                ...styles.stepIndicator,
                backgroundColor: currentStep >= step.number ? '#243444' : '#e0e0e0',
                color: currentStep >= step.number ? '#FFFAFA' : '#4C5454'
              }}
            >
              {step.number}
            </div>
            <div style={{
              ...styles.stepLabel,
              fontWeight: currentStep === step.number ? 'bold' : 'normal',
              color: currentStep === step.number ? '#243444' : '#4C5454'
            }}>
              {step.label}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#FFFAFA',
      padding: '20px'
    },
    logoContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '20px'
    },
    heading: {
      color: '#243444',
      textAlign: 'center',
      marginBottom: '20px',
      fontWeight: 'bold'
    },
    formContainer: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
      padding: '30px',
      width: '100%',
      maxWidth: '500px'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%'
    },
    formGroup: {
      marginBottom: '20px',
      width: '100%'
    },
    label: {
      display: 'block',
      marginBottom: '5px',
      color: '#4C5454',
      fontWeight: 'bold'
    },
    input: {
      width: '100%',
      padding: '10px',
      borderRadius: '4px',
      border: '1px solid #ddd',
      fontSize: '16px'
    },
    select: {
      width: '100%',
      padding: '10px',
      borderRadius: '4px',
      border: '1px solid #ddd',
      fontSize: '16px',
      backgroundColor: 'white'
    },
    button: {
      width: '100%',
      padding: '12px',
      backgroundColor: '#243444',
      color: '#D2BA7B',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'background-color 0.3s'
    },
    backButton: {
      padding: '12px 20px',
      backgroundColor: '#e0e0e0',
      color: '#4C5454',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'background-color 0.3s'
    },
    continueButton: {
      padding: '12px 20px',
      backgroundColor: '#243444',
      color: '#D2BA7B',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'background-color 0.3s'
    },
    paymentContainer: {
      backgroundColor: '#f9f9f9',
      padding: '20px',
      borderRadius: '8px',
      marginBottom: '20px'
    },
    paymentMessage: {
      fontSize: '16px',
      marginBottom: '20px',
      color: '#4C5454',
      textAlign: 'center'
    },
    paymentDetails: {
      backgroundColor: '#FFFAFA',
      padding: '15px',
      borderRadius: '8px',
      marginBottom: '20px',
      lineHeight: '1.6',
      border: '1px solid #D2BA7B'
    },
    paymentButton: {
      width: '100%',
      padding: '14px',
      backgroundColor: '#243444',
      color: '#D2BA7B',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'background-color 0.3s',
      marginBottom: '15px'
    },
    secureNote: {
      fontSize: '14px',
      color: '#4C5454',
      textAlign: 'center'
    },
    navigationButtons: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      marginTop: '20px'
    },
    stepIndicators: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      marginBottom: '30px'
    },
    stepIndicatorContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    },
    stepIndicator: {
      width: '30px',
      height: '30px',
      borderRadius: '50%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: '16px',
      fontWeight: 'bold'
    },
    stepLabel: {
      fontSize: '14px',
      marginTop: '5px',
      textAlign: 'center'
    },
    stepTitle: {
      color: '#243444',
      textAlign: 'center',
      marginBottom: '20px',
      fontWeight: 'bold'
    },
    errorMessage: {
      backgroundColor: '#f8d7da',
      color: '#721c24',
      padding: '10px',
      borderRadius: '4px',
      marginBottom: '20px',
      width: '100%'
    },
    footer: {
      textAlign: 'center',
      marginTop: '20px',
      color: '#4C5454'
    },
    link: {
      color: '#243444',
      textDecoration: 'underline',
      fontWeight: 'bold'
    }
  };

  return (
    <div style={styles.container}>
      {/* Pass isOpen prop to MasterServicesAgreement */}
      <MasterServicesAgreement 
        isOpen={msaModalOpen}
        onClose={() => {
          setMsaModalOpen(false);
          setMsaViewed(true);
        }} 
      />
      
      <div style={styles.formContainer}>
        <div style={styles.logoContainer}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <LogoImage height={60} />
          </Link>
        </div>
        
        <h2 style={styles.heading}>
          {isInvitation ? 'Accept Invitation' : 'Create Your Account'}
        </h2>
        
        {error && <div style={styles.errorMessage}>{error}</div>}
        
        {/* Only show step indicators for regular registration */}
        {!isInvitation && renderStepIndicators()}
        
        {renderStepContent()}
        
        <p style={styles.footer}>
          Already have an account? <Link to="/login" style={styles.link}>Sign In</Link>
        </p>
        <p style={styles.footer}>
          <Link to="/" style={{ color: '#243444', textDecoration: 'none' }}>← Back to Home</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
